import {
  ConeGeometry,
  CylinderGeometry,
  Mesh,
  Object3D,
  PlaneGeometry,
  TorusGeometry,
  Vector3,
} from '@seddi/three';
import { mergeGeometries } from '@seddi/three/examples/jsm/utils/BufferGeometryUtils.js';

import { getNewFovCameraDistance } from '../utils/cameraUtils';
import { GIZMO_TYPES } from '../utils/constants';
import Materials from '../renderer3/materials';

export class Gizmo extends Object3D {
  declare type: string;

  handlers: any[];
  hover: any;
  meshes: { idle: any; hover: any };

  static geometry;

  static setupGeometry() {
    const { hoverScale, intersectScale } = Gizmo;

    let arrows;
    {
      // Arrows
      const cone = new ConeGeometry(0.03, 0.05, 10);
      cone.translate(0, 0.41, 0);
      const line = new CylinderGeometry(0.005, 0.005, 0.5, 4, 4);
      line.translate(0, 0.14, 0);
      const idle = mergeGeometries([
        cone,
        cone.clone().rotateX(Math.PI),
        line,
        line.clone().rotateX(Math.PI),
      ]);

      const lineHover = new CylinderGeometry(
        line.parameters.radiusTop * hoverScale,
        line.parameters.radiusBottom * hoverScale,
        line.parameters.height,
        line.parameters.radialSegments,
        line.parameters.heightSegments,
      );
      lineHover.translate(0, 0.14, 0);
      const hover = mergeGeometries([
        cone,
        cone.clone().rotateX(Math.PI),
        lineHover,
        lineHover.clone().rotateX(Math.PI),
      ]);

      const lineIntersect = new CylinderGeometry(
        line.parameters.radiusTop * intersectScale,
        line.parameters.radiusBottom * intersectScale,
        line.parameters.height,
        line.parameters.radialSegments,
        line.parameters.heightSegments,
      );
      lineIntersect.translate(0, 0.14, 0);
      const intersect = mergeGeometries([
        cone,
        cone.clone().rotateX(Math.PI),
        lineIntersect,
        lineIntersect.clone().rotateX(Math.PI),
      ]);

      arrows = {
        idle,
        hover,
        intersect,
      };
    }

    let torus;
    {
      // Torus
      const radius = 0.3;
      const tube = 0.005;
      const radialSegments = 4;
      const tubularSegments = 64;
      torus = {
        idle: new TorusGeometry(radius, tube, radialSegments, tubularSegments),
        hover: new TorusGeometry(radius, tube * hoverScale, radialSegments, tubularSegments),
        intersect: new TorusGeometry(radius, tube * intersectScale, radialSegments, tubularSegments),
      };
    }

    Gizmo.geometry = { arrows, plane: new PlaneGeometry(0.15, 0.15, 2, 2), torus };
  }

  constructor() {
    super();

    if (!Gizmo.geometry) {
      Gizmo.setupGeometry();
    }

    const geometry = Gizmo.geometry;

    this.handlers = [];
    this.meshes = { idle: {}, hover: {} };
    this.type = GIZMO_TYPES.DEFAULT;

    const addHandler = (action, idleGeometry, hoverGeometry, intersectGeometry, material) => {
      const idle = new Mesh(idleGeometry, material);
      this.meshes.idle[action] = idle;
      this.add(idle);

      if (hoverGeometry) {
        const hover = new Mesh(hoverGeometry, material);
        hover.visible = false;
        this.meshes.hover[action] = hover;
        this.add(hover);
      }

      const handler = new Mesh(intersectGeometry, Materials.Intersect);
      (handler as any).action = action;
      this.handlers.push(handler);
      this.add(handler);
    };

    // Translation
    addHandler(
      'translationX',
      geometry.arrows.idle.clone().rotateZ(Math.PI * -0.5),
      geometry.arrows.hover.clone().rotateZ(Math.PI * -0.5),
      geometry.arrows.intersect.clone().rotateZ(Math.PI * -0.5),
      Materials.Gizmo.X,
    );
    addHandler(
      'translationY',
      geometry.arrows.idle.clone(),
      geometry.arrows.hover.clone(),
      geometry.arrows.intersect.clone(),
      Materials.Gizmo.Y,
    );
    addHandler(
      'translationZ',
      geometry.arrows.idle.clone().rotateX(Math.PI * -0.5),
      geometry.arrows.hover.clone().rotateX(Math.PI * -0.5),
      geometry.arrows.intersect.clone().rotateX(Math.PI * -0.5),
      Materials.Gizmo.Z,
    );
    addHandler(
      'translationXY',
      geometry.plane.clone().translate(0.075, 0.075, 0),
      undefined,
      geometry.plane.clone().translate(0.075, 0.075, 0),
      Materials.Gizmo.XY,
    );
    addHandler(
      'translationXZ',
      geometry.plane
        .clone()
        .rotateX(Math.PI * -0.5)
        .translate(0.075, 0, 0.075),
      undefined,
      geometry.plane
        .clone()
        .rotateX(Math.PI * -0.5)
        .translate(0.075, 0, 0.075),
      Materials.Gizmo.XZ,
    );
    addHandler(
      'translationZY',
      geometry.plane
        .clone()
        .rotateY(Math.PI * -0.5)
        .translate(0, 0.075, 0.075),
      undefined,
      geometry.plane
        .clone()
        .rotateY(Math.PI * -0.5)
        .translate(0, 0.075, 0.075),
      Materials.Gizmo.ZY,
    );

    // Rotation
    addHandler(
      'rotationX',
      geometry.torus.idle.clone().rotateY(Math.PI * 0.5),
      geometry.torus.hover.clone().rotateY(Math.PI * 0.5),
      geometry.torus.intersect.clone().rotateY(Math.PI * 0.5),
      Materials.Gizmo.X,
    );
    addHandler(
      'rotationY',
      geometry.torus.idle.clone().rotateX(Math.PI * -0.5),
      geometry.torus.hover.clone().rotateX(Math.PI * -0.5),
      geometry.torus.intersect.clone().rotateX(Math.PI * -0.5),
      Materials.Gizmo.Y,
    );
    addHandler(
      'rotationZ',
      geometry.torus.idle.clone(),
      geometry.torus.hover.clone(),
      geometry.torus.intersect.clone(),
      Materials.Gizmo.Z,
    );

    this.children[0].onBeforeRender = this.onBeforeRender.bind(this);
    this.visible = false;
  }

  onBeforeRender(renderer, scene, camera) {
    const base = this as Object3D;
    const { position, rotation, scale, visible } = base;

    if (!visible) {
      return;
    }

    camera.getWorldPosition(_vector);
    _vector.sub(position);
    const distance = _vector.length();

    // Calculate the scale based on the distance and FOV
    const originalDistance = getNewFovCameraDistance(camera.fov, distance, 0, 60);
    const fovRatio = originalDistance / distance;

    const scalar = (distance / 4) * fovRatio;
    scale.set(scalar, scalar, scalar);
    const angle = Math.PI * 0.5 - Math.atan2(_vector.z, _vector.x);
    rotation.y = Math.abs(angle) >= Math.PI * 0.5 ? Math.PI : 0;
    base.updateMatrixWorld();
  }

  setHover(action) {
    const base = this as Object3D;
    const {
      meshes: { idle, hover },
    } = this;

    if (!base.visible) {
      return false;
    }

    if (action === this.hover) {
      return false;
    }

    if (this.hover) {
      if (hover[this.hover]) {
        idle[this.hover].visible = true;
        hover[this.hover].visible = false;
      } else if (idle[this.hover].material.transparent) {
        idle[this.hover].material.opacity = 0.5;
      }
    }

    this.hover = action;

    if (action) {
      if (hover[this.hover]) {
        idle[action].visible = false;
        hover[action].visible = true;
      } else if (idle[this.hover].material.transparent) {
        idle[this.hover].material.opacity = 1;
      }
    }

    return true;
  }

  update(visible, matrix = null) {
    this.visible = visible;

    if (this.visible) {
      this.updateWorldMatrix(false, false);
    }
  }

  static hoverScale = 2;
  static intersectScale = 10;
}

const _vector = new Vector3();
